import type { ReactNode } from 'react';
import { Spinner } from '@/components/Spinner';

type QueryStateProps = {
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  loadingFallback?: ReactNode;
  children: ReactNode;
};

/**
 * Standard wrapper for screen-level data fetching. Renders a Spinner (or
 * provided skeleton) while loading, a NL error block with retry CTA on error,
 * and the children otherwise. Keeps the layout-shift consistent because the
 * Spinner area uses the same vertical breathing room.
 */
export function QueryState({
  isLoading,
  isError,
  errorMessage,
  onRetry,
  loadingFallback,
  children,
}: QueryStateProps) {
  if (isLoading) {
    if (loadingFallback) return <>{loadingFallback}</>;
    return (
      <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">Bezig met laden…</span>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center px-6 py-12 text-center" role="alert">
        <h2 className="text-base font-semibold text-slate-900">Kan gegevens niet laden</h2>
        <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-slate-500">
          {errorMessage ??
            'De server gaf een foutmelding terug. Controleer uw verbinding of probeer het opnieuw.'}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-5 rounded bg-slate-900 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-slate-800"
          >
            Opnieuw proberen
          </button>
        )}
      </div>
    );
  }
  return <>{children}</>;
}
